import type { ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { FaXmark as XMark } from 'react-icons/fa6'

interface FullScreenModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
}

/**
 * In-page modal that fills most of the viewport while keeping the
 * user on the project page. Used by the Sequencer tab row's "Full
 * screen" toggle to blow up the currently-active view (grid layout
 * / Piano Roll / Automation / Plugin) without routing away and
 * blowing up the whole workspace context.
 *
 * Structure mirrors `ConfirmDialog.tsx` — Radix `Dialog.Root` +
 * Overlay + Portaled `Dialog.Content` — so behaviour (ESC to close,
 * focus trap, overlay click dismiss) matches the rest of the app.
 *
 * Size: 95vw × 95vh. Fixed for v1; a future follow-up can add
 * resize / drag affordances when the need surfaces.
 */
export function FullScreenModal({
  open,
  onClose,
  title,
  children,
}: FullScreenModalProps) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={next => {
        if (!next) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex w-[95vw] h-[95vh] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-brd-subtle bg-surface-raised shadow-high focus:outline-none"
          aria-describedby={undefined}
        >
          <header className="flex items-center justify-between gap-2 border-b border-brd-subtle px-4 py-2.5">
            <Dialog.Title className="text-xs font-mono uppercase tracking-wider text-content-secondary">
              {title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close full screen"
                className="rounded p-1.5 text-content-secondary transition-colors hover:bg-surface-sunken hover:text-content-primary"
                title="Close (ESC)"
              >
                <XMark size={14} />
              </button>
            </Dialog.Close>
          </header>
          <div className="min-h-0 flex-1 overflow-auto p-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
